import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { reviewsData, shopInfo } from '../data/menuData';

const ReviewsSection = () => {
  const { language, t } = useLanguage();

  // Duplicate reviews for infinite scroll effect
  const duplicatedReviews = [...reviewsData, ...reviewsData];

  return (
    <section id="reviews" className="py-20 bg-[#1A1816] overflow-hidden" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FDFBF7] mb-4">
            {t('reviews.title')}
          </h2>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={24}
                  className={i < Math.floor(shopInfo.rating) ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-gray-600'}
                />
              ))}
            </div>
            <span className="font-playfair text-2xl font-bold text-[#FDFBF7]">{shopInfo.rating}</span>
          </div>
          <p className="font-manrope text-lg text-[#9F3E1F] font-semibold">
            {t('reviews.subtitle')}
          </p>
          <div className="w-24 h-1 bg-[#9F3E1F] mx-auto rounded-full mt-6" />
        </motion.div>
      </div>

      {/* Infinite Scroll Reviews */}
      <div className="relative">
        {/* First Row - Left to Right */}
        <motion.div
          animate={{ x: ['0%', '-50%'] }}
          transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          className="flex gap-6 mb-6"
        >
          {duplicatedReviews.map((review, index) => (
            <ReviewCard key={`row1-${index}`} review={review} language={language} />
          ))}
        </motion.div>

        {/* Second Row - Right to Left */}
        <motion.div
          animate={{ x: ['-50%', '0%'] }}
          transition={{ duration: 35, repeat: Infinity, ease: 'linear' }}
          className="flex gap-6"
        >
          {duplicatedReviews.reverse().map((review, index) => (
            <ReviewCard key={`row2-${index}`} review={review} language={language} />
          ))}
        </motion.div>
      </div>

      {/* Google Reviews Link */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mt-12"
      >
        <motion.a
          href={shopInfo.googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#2D2A26] text-[#FDFBF7] px-6 py-3 rounded-full font-manrope font-medium hover:bg-[#3D3A36] transition-colors border border-[#FDFBF7]/10"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="google-reviews-link"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {language === 'nl' ? 'Bekijk alle reviews op Google' : 'View all reviews on Google'}
        </motion.a>
      </motion.div>
    </section>
  );
};

const ReviewCard = ({ review, language }) => {
  return (
    <div
      className="flex-shrink-0 w-80 bg-[#2D2A26] rounded-2xl p-6 shadow-lg border border-[#FDFBF7]/5"
      data-testid={`review-card-${review.id}`}
    >
      <div className="flex items-center gap-1 mb-3">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={16}
            className={i < review.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-gray-600'}
          />
        ))}
      </div>
      <p className="font-manrope text-[#FDFBF7] mb-4 line-clamp-3">
        "{review.text[language]}"
      </p>
      <div className="flex items-center justify-between">
        <span className="font-manrope font-semibold text-[#FDFBF7]">{review.name}</span>
        <span className="font-manrope text-sm text-[#FDFBF7]/50">{review.date}</span>
      </div>
    </div>
  );
};

export default ReviewsSection;
